import { SaveIcon } from '@systemeio/ui/icons'
import { BreadcrumbsData, DateRangePicker, DateTimePicker, FormInput, PrimaryButton, toast } from '@systemeio/ui'
import { InternalError } from '@systemeio/util'
import { COUPONS_API, updateCoupon } from 'modules/coupons/api/coupon-api'
import CouponEditSkeleton from 'modules/coupons/components/coupon-edit-skeleton'
import { CouponLoco } from 'modules/coupons/constants/coupon-loco'
import { useCoupon } from 'modules/coupons/hooks/use-coupon'
import { useCouponFunnels } from 'modules/coupons/hooks/use-coupon-funnels'
import { useCouponSales } from 'modules/coupons/hooks/use-coupon-sales'
import {
  CouponFunnelInterface,
  CouponSalesInterface,
} from 'modules/coupons/types/coupon-edit-interface'
import { CouponSalesFilterInterface } from 'modules/coupons/types/coupon-filter-interface'
import {
  CouponApiInterface,
  CouponCreateInterface,
  CouponErrorsInterface,
} from 'modules/coupons/types/coupon-interface'
import { getFunnelStepUrl } from 'modules/funnels/funnel/utils/get-funnel-step-url'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useMutationUpdate } from 'shared/hooks/use-mutation-update'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { dateTimeFormat, filterPassedTime } from 'shared/utils/date-time-format'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { getToday } from 'shared/utils/get-dates'
import { PricePlanTooltip } from 'shared/components/tooltip/price-plan-tooltip'

interface CouponEditErrorsInterface
  extends Pick<CouponErrorsInterface, 'expirationDate' | 'innerName' | 'limitOfUse'> {}

export interface CouponUpdateInterface
  extends Pick<CouponCreateInterface, 'innerName' | 'expirationDate' | 'limitOfUse'> {}

const defaultErrors: CouponEditErrorsInterface = {
  innerName: '',
  expirationDate: '',
  limitOfUse: '',
}

const defaultValues: CouponUpdateInterface = {
  limitOfUse: null,
  expirationDate: null,
  innerName: '',
}

const defaultFilters: CouponSalesFilterInterface = { date: [null, null] }

const CouponEdit: NextPage = () => {
  const router = useRouter()
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const mutationUpdate = useMutationUpdate()
  const { couponId } = router.query
  const { data: coupon, mutate } = useCoupon(couponId as string)
  const {
    setPagination: setPaginationSales,
    pagination: paginationSales,
    setLimit: setLimitSales,
    limit: limitSales,
    filter,
    setFilter,
  } = useFilterPagination(defaultFilters, true)

  const {
    setPagination: setPaginationFunnels,
    pagination: paginationFunnels,
    setLimit: setLimitFunnels,
    limit: limitFunnels,
  } = usePagination(true)

  const { data: sales } = useCouponSales({
    limit: limitSales,
    pagination: paginationSales,
    filter,
    id: couponId as string,
  })

  const { data: funnels } = useCouponFunnels({
    limit: limitFunnels,
    pagination: paginationFunnels,
    id: couponId as string,
  })

  const updateData = useUpdateData({
    api: COUPONS_API,
    constantApi: [funnels, sales, `${COUPONS_API}/list`],
  })

  const breadcrumbsData: BreadcrumbsData[] = [
    { caption: t('dashboard.coupon.title_plural'), href: '/coupons', isStatic: true },
    { caption: `${coupon?.innerName} (${coupon?.code})` },
  ]

  const [isFetching, setIsFetching] = useState(false)

  const isDisabled = () => name === defaultValues.innerName

  const [name, setName] = useState<CouponCreateInterface['innerName']>(defaultValues.innerName)
  const [date, setDate] = useState<CouponCreateInterface['expirationDate']>(
    defaultValues.expirationDate,
  )
  const [limitOfUse, setLimitOfUse] = useState<CouponCreateInterface['limitOfUse']>(
    defaultValues.limitOfUse,
  )

  useEffect(() => {
    if (coupon) {
      setName(coupon.innerName)
      setDate(coupon.expirationDate)
      setLimitOfUse(coupon.limitOfUse)
    }
  }, [coupon])

  const [errors, setErrors] = useState<CouponEditErrorsInterface>(defaultErrors)

  const onSave = async () => {
    try {
      await fetchWithErrors(
        async () => {
          const data: CouponUpdateInterface = {
            limitOfUse: limitOfUse,
            expirationDate: date,
            innerName: name,
          }
          setIsFetching(true)
          const response = await updateCoupon(coupon?.id || 0, data)
          await updateData(
            false,
            undefined,
            {
              mutationData: coupon,
              onMutate: async () => {
                await mutate({ ...response.data }, false)
              },
            },
            {
              id: coupon?.id,
              onMutate: async apiKey => {
                await mutationUpdate<CouponApiInterface>(
                  apiKey,
                  data =>
                    data && {
                      hasMore: data.hasMore,
                      items: data.items.map(item => {
                        if (item.id === response.data.id) item = response.data
                        return item
                      }),
                    },
                )
              },
            },
          )
          setIsFetching(false)
          toast.success(t('global.changes_saved'))
        },
        setErrors,
        () => setIsFetching(false),
      )
    } catch (e) {
      if (e instanceof InternalError) {
        toast.error(t('core.error.internal_error_message'))
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  const funnelsColumns: ColumnDefinitionInterface<CouponFunnelInterface>[] = [
    {
      key: 'funnel',
      header: t('dashboard.coupon.funnel_step'),
      widthPercent: 100,
      onRender: funnelStep => (
        <div>
          <LinkWithoutPrefetch
            rel="noreferrer"
            className="primary-link w-full"
            href={getFunnelStepUrl(funnelStep.funnel.id, funnelStep.id)}
          >
            {`${funnelStep.funnel?.name}: ${funnelStep.name}`}
          </LinkWithoutPrefetch>
        </div>
      ),
    },
  ]

  const salesColumns: ColumnDefinitionInterface<CouponSalesInterface>[] = [
    {
      key: 'pricePlan',
      header: t('dashboard.coupon.price_plan_inner_name'),
      widthPercent: 50,
      onRender: sale =>
        sale.pricePlan ? (
          <PricePlanTooltip pricePlan={sale.pricePlan}>
            <span className={'cursor-pointer'}>{sale.pricePlan.innerName}</span>
          </PricePlanTooltip>
        ) : (
          <></>
        ),
    },
    {
      key: 'order',
      header: t('global.customer'),
      widthPercent: 30,
      onRender: sale => (
        <div className="flex w-full flex-col">
          <span className="text-text-primary">{sale.order.customer.fullName}</span>
          <div className="truncate">
            {sale.order.customer.contact === null ? (
              sale.order.customer.email
            ) : (
              <LinkWithoutPrefetch
                className="primary-link w-full"
                href={`/contacts/${sale.order.customer.contact?.id}`}
              >
                {sale.order.customer.email}
              </LinkWithoutPrefetch>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'order',
      header: t('dashboard.coupon.date_of_sale'),
      widthPercent: 20,
      onRender: sale => (
        <span>
          {dateTimeFormat(sale.order.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]

  return (
    <>
      <div className={`flex flex-col gap-10`}>
        <div className="flex items-center justify-between gap-5">
          <BreadcrumbsDashboard data={breadcrumbsData} isFetching={!coupon} />
          <PrimaryButton
            onClick={onSave}
            isFetching={isFetching}
            disabled={isDisabled() || !coupon}
          >
            <SaveIcon />
            <span>{t('global.save')}</span>
          </PrimaryButton>
        </div>

        <>
          {coupon ? (
            <div className={`flex flex-col overflow-x-hidden rounded-lg`}>
              <div
                className={`bg-brand-primary-subtle text-text-primary flex px-6 py-4 font-bold`}
              >{`${t('dashboard.coupon.code')}: ${coupon?.code}`}</div>
              <div
                className={`bg-background-secondary flex flex-col flex-wrap gap-5 p-6 lg:flex-row`}
              >
                <FormInput
                  value={name}
                  onChange={e => setName(e.target.value)}
                  label={t('dashboard.coupon.coupon_name_edit')}
                  required
                  error={errors.innerName}
                  className={'flex-auto'}
                />
                <DateTimePicker
                  value={date}
                  onChange={date => setDate(date)}
                  label={t('dashboard.coupon.coupon_expiration_date')}
                  customProps={{
                    minDate: getToday(user?.timezone),
                    filterTime: time => filterPassedTime(time, user?.timezone),
                  }}
                  error={errors.expirationDate}
                  wrapperButtonInputClassName={'flex-initial lg:min-w-[300px]'}
                  createdAt={user?.createdAt}
                  timezone={user?.timezone}
                  intlLocale={intlLocale}
                />
                <FormInput
                  label={t('dashboard.coupon.coupon_limit_of_use')}
                  type="number"
                  onChange={e => setLimitOfUse(e.target.valueAsNumber)}
                  value={!limitOfUse && limitOfUse !== 0 ? '' : limitOfUse}
                  maxLength={256}
                  min={0}
                  error={errors.limitOfUse}
                  description={t('dashboard.coupon.leave_blank')}
                  className={'flex-initial'}
                />
              </div>
            </div>
          ) : (
            <CouponEditSkeleton />
          )}
          <div className="lg:flex lg:flex-wrap lg:gap-10">
            <div className="flex-1">
              <ListingLayout
                counter={false}
                data={funnels}
                title={t('dashboard.coupon.tied_funnels')}
                type={'compact'}
              >
                <Table
                  data={funnels}
                  columns={funnelsColumns}
                  limiter={{
                    limit: limitFunnels,
                    setLimit: setLimitFunnels,
                  }}
                  paginator={{
                    pagination: paginationFunnels,
                    setPagination: setPaginationFunnels,
                  }}
                />
              </ListingLayout>
            </div>
            <div className="flex-2 mt-10 lg:mt-0">
              <ListingLayout
                counter={false}
                data={sales}
                title={t('dashboard.coupon.sales_with_coupon')}
                type={'default'}
                filterData={{
                  filterer: { filter, setFilter, defaultFilters },
                  paginator: { pagination: paginationSales, setPagination: setPaginationSales },
                  limiter: { limit: limitSales, setLimit: setLimitSales },
                  filterable: [
                    {
                      field: 'date',
                      onRender: (value, onChange) => (
                        <DateRangePicker
                          key={'coupon-sale-date-filter'}
                          date={value}
                          onChange={value => onChange(value)}
                          timezone={user?.timezone}
                          createdAt={user?.createdAt}
                          intlLocale={intlLocale}
                        />
                      ),
                    },
                  ],
                }}
              >
                <Table
                  data={sales}
                  columns={salesColumns}
                  limiter={{
                    limit: limitSales,
                    setLimit: setLimitSales,
                  }}
                  paginator={{
                    pagination: paginationSales,
                    setPagination: setPaginationSales,
                  }}
                />
              </ListingLayout>
            </div>
          </div>
        </>
      </div>
    </>
  )
}

export const getServerSideProps = getBaseServerSideProps(CouponLoco, 'header.top_menu.coupons')

export default CouponEdit
